import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../../contexts/NotificationContext';

interface PendingModification {
  id: number;
  intervention_id: number;
  type_action: 'UPDATE' | 'DELETE' | 'COMPLETED';
  donnees_originales: any;
  donnees_modifiees: any;
  motif: string;
  demande_par_utilisateur_id: number;
  demande_par_role: string;
  cree_le: string;
  campagne_nom: string;
  contact: string;
  numero_elan: string;
}

interface LineManagementModalProps {
  show: boolean;
  onClose: () => void;
  onRefresh?: () => void;
}

interface EmailPreviewData {
  recipient: string;
  subject: string;
  htmlContent: string;
  modificationId?: number;
  campaignName?: string;
}

// ===== Composant LineManagementModal =====
const LineManagementModal: React.FC<LineManagementModalProps> = ({ show, onClose, onRefresh }) => {
  const [pendingMods, setPendingMods] = useState<PendingModification[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [manualEmailData, setManualEmailData] = useState<EmailPreviewData | null>(null);
  const [manualRecipient, setManualRecipient] = useState('');
  const [manualSubject, setManualSubject] = useState('');
  const { showNotification } = useNotification();

  const POWER_AUTOMATE_WEBHOOK_URL = 'https://default4a7c823857994b169fc69ad8fce5a7.d9.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/a51b6e3b094d4fd19377cd1b64e45f85/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=ZKGVMxBOqNVec0EnMcP-jKfNdsz4MCrM_2I0Xmb7VfA';

  const sendEmailViaAutomate = async (emailData: EmailPreviewData): Promise<boolean> => {
    if (!emailData.recipient.trim()) {
      showNotification('Veuillez saisir une adresse email', 'error');
      return false;
    }

    setSendingEmail(true);
    try {
      const payload = {
        to: emailData.recipient,
        from: 'mn_gof_stf-infra@sncf.fr',
        cc: ['MN_GOF_STF-INFRA@sncf.fr'],
        subject: emailData.subject,
        htmlBody: emailData.htmlContent,
        timestamp: new Date().toISOString()
      };

      const response = await fetch(POWER_AUTOMATE_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      showNotification('Email envoyé avec succès via Power Automate', 'success');

      try {
        await fetch('/api/email/log-power-automate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            recipient: emailData.recipient,
            subject: emailData.subject,
            campaign: emailData.campaignName || 'Modification',
            intervention_count: 1
          })
        });
      } catch {
      }

      return true;
    } catch (error: any) {
      console.error('Erreur envoi Power Automate:', error);
      showNotification('Erreur lors de l\'envoi: ' + (error.message || 'Erreur inconnue'), 'error');
      return false;
    } finally {
      setSendingEmail(false);
    }
  };

  const openManualEmailModal = (emailData: EmailPreviewData) => {
    setManualEmailData(emailData);
    setManualRecipient(emailData.recipient || '');
    setManualSubject(emailData.subject || '');
  };

  const closeManualEmailModal = () => {
    setManualEmailData(null);
    setManualRecipient('');
    setManualSubject('');
  };

  const loadPendingModifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/pending-modifications/pending', {
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || `Erreur serveur: ${response.status} ${response.statusText}`;
        showNotification(errorMessage, 'error');
        return;
      }

      const data = await response.json();
      setPendingMods(data);
    } catch (error) {
      console.error('Error loading pending modifications:', error);
      const message = error instanceof Error ? error.message : 'Erreur inconnue lors du chargement';
      showNotification(message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    if (show) {
      void loadPendingModifications();
    }
  }, [show, loadPendingModifications]);

  const handleReview = async (id: number, decision: 'APPROVED' | 'REJECTED'): Promise<boolean> => {
    try {
      const response = await fetch(`/api/pending-modifications/review/${id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          decision,
          motif_revision: ''
        })
      });

      if (response.ok) {
        await response.json();
        showNotification(
          decision === 'APPROVED' ? 'Modification approuvée' : 'Modification rejetée',
          'success'
        );
        void loadPendingModifications();
        onRefresh?.();

        window.dispatchEvent(new CustomEvent('interventionsUpdated'));
        window.dispatchEvent(new CustomEvent('pendingModificationsUpdated'));
        return true;
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || `Erreur serveur: ${response.status} ${response.statusText}`;
        showNotification(errorMessage, 'error');
        return false;
      }
    } catch (error) {
      console.error('Error reviewing modification:', error);
      const message = error instanceof Error ? error.message : 'Erreur inconnue lors de la révision';
      showNotification(message, 'error');
      return false;
    }
  };

  const buildEmailPreviewData = (mod: PendingModification): EmailPreviewData | null => {
    if (mod.type_action !== 'UPDATE') return null;

    try {
      const originalData = JSON.parse(mod.donnees_originales);
      const modifiedData = JSON.parse(mod.donnees_modifiees);
      const tcEmail = originalData.technicentre_email || modifiedData.technicentre_email || '';

      const subject = `Modification Intervention SET LGV - ${mod.numero_elan || ''} - ${mod.campagne_nom || ''}`;

      const fields = [
        { key: 'contact', label: 'Contact' },
        { key: 'disponibilite_a_partir', label: 'Disponibilité', isDate: true },
        { key: 'depart_pour_mission', label: 'Départ mission', isDate: true },
        { key: 'technicentre_nom', label: 'Technicentre' },
        { key: 'site_nom', label: 'Lieu' },
        { key: 'annulation_client', label: 'Annulation Client', isBool: true },
        { key: 'engin_1', label: 'Engin 1' },
        { key: 'engin_2', label: 'Engin 2' },
        { key: 'engin_3', label: 'Engin 3' },
        { key: 'engin_4', label: 'Engin 4' }
      ];

      let changesHtml = '';
      fields.forEach(field => {
        const normalizeVal = (v: any) => (v === null || v === undefined || v === '') ? '' : String(v);
        if (normalizeVal((originalData as any)[field.key]) !== normalizeVal((modifiedData as any)[field.key])) {
          let oldVal = (originalData as any)[field.key];
          let newVal = (modifiedData as any)[field.key];

          if ((field as any).isBool) {
            oldVal = oldVal ? 'Oui' : 'Non';
            newVal = newVal ? 'Oui' : 'Non';
          } else if ((field as any).isDate && oldVal) {
            oldVal = formatDate(oldVal);
          }
          if ((field as any).isDate && newVal) {
            newVal = formatDate(newVal);
          }

          changesHtml += `
            <tr>
              <td style="padding:8px 12px;border:1px solid #e5e7eb;font-weight:600;color:#374151;">${field.label}</td>
              <td style="padding:8px 12px;border:1px solid #e5e7eb;color:#dc2626;text-decoration:line-through;">${oldVal || '(vide)'}</td>
              <td style="padding:8px 12px;border:1px solid #e5e7eb;color:#059669;font-weight:600;">${newVal || '(vide)'}</td>
            </tr>`;
        }
      });

      const htmlContent = `
<!DOCTYPE html>
<html>
<head><meta charset="UTF-8"><title>${subject}</title></head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif;background-color:#f3f4f6;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f3f4f6;">
    <tr>
      <td align="center" style="padding:20px 0;">
        <table width="700" cellpadding="0" cellspacing="0" border="0" style="background-color:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 4px 6px rgba(0,0,0,0.1);">
          <tr>
            <td style="background:#f97316;padding:25px;text-align:center;">
              <h1 style="margin:0;color:#ffffff;font-size:24px;font-weight:bold;">Modification Intervention SET LGV</h1>
            </td>
          </tr>
          <tr>
            <td style="padding:25px;">
              <p style="margin:0 0 15px 0;color:#374151;font-size:16px;">Bonjour,</p>
              <p style="margin:0 0 20px 0;color:#374151;font-size:16px;">Le client a modifié la demande d'intervention suivante, initialement :</p>
              <table style="margin-bottom:20px;width:100%;background-color:#f9fafb;border-radius:6px;padding:15px;" cellpadding="8">
                <tr><td style="color:#6b7280;font-size:14px;padding:4px 12px;"><strong>Campagne :</strong> ${originalData.campagne_nom || '-'}</td></tr>
                <tr><td style="color:#6b7280;font-size:14px;padding:4px 12px;"><strong>N° ELAN :</strong> ${originalData.numero_elan || '-'}</td></tr>
                <tr><td style="color:#6b7280;font-size:14px;padding:4px 12px;"><strong>Contact :</strong> ${originalData.contact || '-'}</td></tr>
                <tr><td style="color:#6b7280;font-size:14px;padding:4px 12px;"><strong>Disponibilité :</strong> ${originalData.disponibilite_a_partir ? formatDate(originalData.disponibilite_a_partir) : '-'}</td></tr>
                <tr><td style="color:#6b7280;font-size:14px;padding:4px 12px;"><strong>Départ mission :</strong> ${originalData.depart_pour_mission ? formatDate(originalData.depart_pour_mission) : '-'}</td></tr>
                <tr><td style="color:#6b7280;font-size:14px;padding:4px 12px;"><strong>Lieu :</strong> ${originalData.site_nom || '-'}</td></tr>
                <tr><td style="color:#6b7280;font-size:14px;padding:4px 12px;"><strong>Date d'intervention :</strong> ${originalData.date_intervention ? formatDate(originalData.date_intervention) : '-'}</td></tr>
                <tr><td style="color:#6b7280;font-size:14px;padding:4px 12px;"><strong>Numéros d'engins :</strong> ${[originalData.engin_1, originalData.engin_2, originalData.engin_3, originalData.engin_4].filter(Boolean).join(', ') || '-'}</td></tr>
              </table>
              <h3 style="margin:20px 0 15px 0;color:#374151;font-size:18px;border-bottom:2px solid #f97316;padding-bottom:8px;">Modifications actées :</h3>
              <table width="100%" cellpadding="0" cellspacing="0" border="1" style="border-collapse:collapse;border:1px solid #e5e7eb;">
                <tr style="background-color:#f9fafb;">
                  <th style="padding:10px 12px;font-size:13px;font-weight:600;color:#374151;text-align:left;border:1px solid #e5e7eb;">Champ</th>
                  <th style="padding:10px 12px;font-size:13px;font-weight:600;color:#374151;text-align:left;border:1px solid #e5e7eb;">Précédemment</th>
                  <th style="padding:10px 12px;font-size:13px;font-weight:600;color:#374151;text-align:left;border:1px solid #e5e7eb;">Actuellement</th>
                </tr>
                ${changesHtml}
              </table>
              <p style="margin:25px 0 10px 0;color:#374151;font-size:14px;line-height:1.6;">Pour prise en compte SVP.</p>
              <p style="margin:0 0 15px 0;color:#374151;font-size:14px;line-height:1.6;">Si cette modification impacte vos possibilités d'intervention, merci de nous en informer via le site <a href="https://lgv.uochalindrey.fr/" style="color:#f97316;text-decoration:none;font-weight:600;">https://lgv.uochalindrey.fr/</a>.</p>
              <p style="margin:20px 0 0 0;color:#374151;font-size:15px;">Cordialement,<br><strong>L'équipe STF</strong></p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

      return {
        recipient: tcEmail,
        subject,
        htmlContent,
        modificationId: mod.id,
        campaignName: mod.campagne_nom
      };
    } catch (error) {
      console.error('Erreur génération email de modification:', error);
      return null;
    }
  };

  const handleAcknowledgeAndNotify = async (mod: PendingModification) => {
    if (mod.type_action === 'UPDATE') {
      const emailData = buildEmailPreviewData(mod);
      if (!emailData) {
        showNotification('Impossible de générer l\'email de notification', 'error');
        return;
      }

      if (!emailData.recipient.trim()) {
        openManualEmailModal(emailData);
        showNotification('Aucune adresse email SR par défaut. Merci de la renseigner.', 'warning');
        return;
      }

      const sent = await sendEmailViaAutomate(emailData);
      if (!sent) {
        return;
      }
    }

    await handleReview(mod.id, 'APPROVED');
  };

  const handleManualEmailSend = async () => {
    if (!manualEmailData) return;

    const trimmedRecipient = manualRecipient.trim();
    if (!trimmedRecipient) {
      showNotification('Veuillez saisir une adresse email', 'error');
      return;
    }

    const emailDataToSend: EmailPreviewData = {
      ...manualEmailData,
      recipient: trimmedRecipient,
      subject: manualSubject.trim() || manualEmailData.subject
    };

    const sent = await sendEmailViaAutomate(emailDataToSend);
    if (!sent) {
      return;
    }

    if (!manualEmailData.modificationId) {
      showNotification('Impossible de valider: identifiant de modification manquant', 'error');
      return;
    }

    const reviewed = await handleReview(manualEmailData.modificationId, 'APPROVED');
    if (reviewed) {
      closeManualEmailModal();
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      const normalizedDateString = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(dateString)
        ? `${dateString.replace(' ', 'T')}Z`
        : dateString;
      const date = new Date(normalizedDateString);
      if (isNaN(date.getTime())) return dateString;

      const formatter = new Intl.DateTimeFormat('fr-FR', {
        timeZone: 'Europe/Paris',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });

      return formatter.format(date).replace(',', '');
    } catch {
      return dateString;
    }
  };

  const renderComparisonData = (original: any, modified: any) => {
    if (!modified) return null;

    const fields = [
      { key: 'contact', label: 'Contact', isDate: false },
      { key: 'campagne_nom', label: 'Campagne', isDate: false },
      { key: 'site_nom', label: 'Site', isDate: false },
      { key: 'lieu_dit', label: 'Lieu dit', isDate: false },
      { key: 'prestation_nom', label: 'Prestation', isDate: false },
      { key: 'disponibilite_a_partir', label: 'Disponibilité', isDate: true },
      { key: 'numero_elan', label: 'N° ELAN', isDate: false },
      { key: 'depart_pour_mission', label: 'Départ mission', isDate: true },
      { key: 'technicentre_nom', label: 'Technicentre', isDate: false },
      { key: 'annulation_client', label: 'Annulation Client', isDate: false },
      { key: 'engin_1', label: 'Engin 1', isDate: false },
      { key: 'engin_2', label: 'Engin 2', isDate: false },
      { key: 'engin_3', label: 'Engin 3', isDate: false },
      { key: 'engin_4', label: 'Engin 4', isDate: false }
    ];

    const normalizeDateForComparison = (dateString: any) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.getTime().toString();
      } catch {
        return String(dateString);
      }
    };

    const normalizeValue = (value: any) => {
      if (value === null || value === undefined || value === '') return '';
      return String(value);
    };

    const changedFields = fields.filter(field => {
      const origValue = original[field.key];
      const modValue = modified[field.key];

      if (field.isDate) {
        return normalizeDateForComparison(origValue) !== normalizeDateForComparison(modValue);
      }

      return normalizeValue(origValue) !== normalizeValue(modValue);
    });

    if (changedFields.length === 0) return null;

    const formatValue = (value: any, isDate: boolean, fieldKey?: string) => {
      if (value === null || value === undefined || value === '') return '(vide)';
      if (isDate) {
        return formatDate(value);
      }
      if (fieldKey === 'annulation_client') {
        return (value === 1 || value === true || value === '1') ? 'Oui' : 'Non';
      }
      return value;
    };

    return (
      <div className="mt-4">
        <h4 className="font-weight-600 text-dark mb-2">Modifications proposées :</h4>
        <div className="space-y-2">
          {changedFields.map(field => (
            <div key={field.key} className="flex justify-between items-center p-2 bg-gray-50 rounded">
              <span className="font-weight-600 text-gray">{field.label}:</span>
              <div className="flex items-center space-x-2">
                <span className="text-danger text-sm font-medium">
                  {formatValue(original[field.key], field.isDate, field.key)}
                </span>
                <span className="text-light">→</span>
                <span className="text-secondary font-weight-600 text-sm">
                  {formatValue(modified[field.key], field.isDate, field.key)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (!show) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose}>
        <div className="modal-content modal-xl" onClick={e => e.stopPropagation()}>
        <div className="relative mb-6">
          <button onClick={onClose} className="modal-close">
            <i className="fas fa-times"></i>
          </button>
          <div className="flex items-center">
            <i className="fas fa-tasks text-2xl text-orange mr-3"></i>
            <h2 className="text-2xl font-bold text-dark">Gestion des lignes</h2>
            {pendingMods.length > 0 && (
              <span className="ml-3 badge badge-danger">
                {pendingMods.length} en attente
              </span>
            )}
          </div>
        </div>

        <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <span className="ml-3 text-gray-600">Chargement...</span>
            </div>
          ) : pendingMods.length === 0 ? (
            <div className="text-center py-12">
              <i className="fas fa-check-circle text-green-400 text-6xl mb-4"></i>
              <h3 className="text-xl font-medium text-gray-600 mb-2">Aucune modification en attente</h3>
              <p className="text-gray-500">Toutes les modifications ont été traitées</p>
            </div>
          ) : (
            <div className="p-6 space-y-4">
              {pendingMods.map((mod) => (
                <div key={mod.id} className="border border-light rounded-lg p-4 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className={`badge ${
                          mod.type_action === 'DELETE'
                            ? 'badge-danger'
                            : mod.type_action === 'COMPLETED'
                            ? 'badge-success'
                            : 'badge-info'
                        }`}>
                          {mod.type_action === 'DELETE'
                            ? 'Suppression'
                            : mod.type_action === 'COMPLETED'
                            ? 'Terminée'
                            : 'Modification'}
                        </span>
                        <span className="ml-2 text-sm text-light">
                          {mod.type_action === 'COMPLETED'
                            ? `Terminée le ${formatDate(mod.cree_le)}`
                            : `Demandée le ${formatDate(mod.cree_le)}`}
                        </span>
                      </div>

                      {(() => {
                        const originalData = mod.donnees_originales ? JSON.parse(mod.donnees_originales) : {};
                        const engins = [originalData.engin_1, originalData.engin_2, originalData.engin_3, originalData.engin_4].filter(Boolean).join(', ');
                        return (
                          <>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                              <div>
                                <span className="text-sm font-weight-600 text-gray">Campagne:</span>
                                <p className="text-sm text-dark">{mod.campagne_nom || '-'}</p>
                              </div>
                              <div>
                                <span className="text-sm font-weight-600 text-gray">N° ELAN:</span>
                                <p className="text-sm text-dark">{mod.numero_elan || '-'}</p>
                              </div>
                              <div>
                                <span className="text-sm font-weight-600 text-gray">Contact:</span>
                                <p className="text-sm text-dark">{mod.contact || '-'}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                              <div>
                                <span className="text-sm font-weight-600 text-gray">Disponibilité:</span>
                                <p className="text-sm text-dark">{originalData.disponibilite_a_partir ? formatDate(originalData.disponibilite_a_partir) : '-'}</p>
                              </div>
                              <div>
                                <span className="text-sm font-weight-600 text-gray">Départ mission:</span>
                                <p className="text-sm text-dark">{originalData.depart_pour_mission ? formatDate(originalData.depart_pour_mission) : '-'}</p>
                              </div>
                              <div>
                                <span className="text-sm font-weight-600 text-gray">Lieu:</span>
                                <p className="text-sm text-dark">{originalData.site_nom || '-'}</p>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                              <div>
                                <span className="text-sm font-weight-600 text-gray">Date d'intervention:</span>
                                <p className="text-sm text-dark">{originalData.date_intervention ? formatDate(originalData.date_intervention) : '-'}</p>
                              </div>
                              <div>
                                <span className="text-sm font-weight-600 text-gray">Numéros d'engins:</span>
                                <p className="text-sm text-dark">{engins || '-'}</p>
                              </div>
                              <div>
                                <span className="text-sm font-weight-600 text-gray">Technicentre:</span>
                                <p className="text-sm text-dark">{originalData.technicentre_nom || '-'}</p>
                              </div>
                            </div>
                          </>
                        );
                      })()}

                      <div className="mb-3">
                        <span className="text-sm font-weight-600 text-gray">Raison:</span>
                        <p className="text-sm text-dark mt-1 p-2 bg-gray-100 rounded">{mod.motif}</p>
                      </div>

                      {mod.type_action === 'UPDATE' && renderComparisonData(
                        JSON.parse(mod.donnees_originales),
                        JSON.parse(mod.donnees_modifiees)
                      )}
                    </div>

                    {mod.type_action !== 'COMPLETED' && (
                      <div className="ml-4 flex flex-col space-y-2">
                        <button
                          onClick={() => void handleAcknowledgeAndNotify(mod)}
                          disabled={sendingEmail}
                          className="btn btn-secondary"
                        >
                          <i className="fas fa-check mr-1"></i>
                          {sendingEmail ? 'Notification en cours...' : 'Bien pris en compte, je notifie le SR'}
                        </button>
                      </div>
                    )}
                    {mod.type_action === 'COMPLETED' && (
                      <div className="ml-4 flex items-center">
                        <span className="text-success font-weight-600">
                          <i className="fas fa-check-circle mr-1"></i>
                          Intervention terminée
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        </div>
      </div>

      {manualEmailData && (
        <div className="modal-backdrop" onClick={closeManualEmailModal}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <div className="relative mb-6">
              <button onClick={closeManualEmailModal} className="modal-close">
                <i className="fas fa-times"></i>
              </button>
              <div className="flex items-center">
                <i className="fas fa-envelope text-2xl text-orange mr-3"></i>
                <h3 className="text-2xl font-bold text-dark">Adresse email SR</h3>
              </div>
            </div>

            <p className="text-gray-600 mb-4">
              Aucune adresse email par défaut n&apos;est renseignée. Merci de saisir l&apos;adresse destinataire.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-weight-600 text-gray mb-2">Destinataire</label>
                <input
                  type="email"
                  className="form-input w-full"
                  placeholder="prenom.nom@sncf.fr"
                  value={manualRecipient}
                  onChange={(e) => setManualRecipient(e.target.value)}
                  disabled={sendingEmail}
                />
              </div>

              <div>
                <label className="block text-sm font-weight-600 text-gray mb-2">Objet</label>
                <input
                  type="text"
                  className="form-input w-full"
                  value={manualSubject}
                  onChange={(e) => setManualSubject(e.target.value)}
                  disabled={sendingEmail}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={closeManualEmailModal}
                disabled={sendingEmail}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => void handleManualEmailSend()}
                disabled={sendingEmail}
              >
                {sendingEmail ? 'Envoi en cours...' : 'Envoyer et valider'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LineManagementModal;
